import { useQuery } from "@tanstack/react-query";
import {
  EMPTY_JOB_SEARCH_FILTER,
  NOTIFICATION_JOB_LIMIT,
  notificationFeed,
  notificationText,
  type NotificationFeedLine,
  type NotificationLine,
} from "@makit/shared";

import { Button, Skeleton, SourceBadge } from "../../components/primitives";
import { LimitBanner } from "../../components/banners";
import { EmptyState } from "../../components/feedback";
import { listJobs } from "../../lib/api";
import { useNotificationReads, useNotifications } from "./api";

export interface NotificationsPanelProps {
  /** 줄을 누르면 부른다. 주소는 `notificationHref` 가 만든다. */
  onOpen: (href: string) => void;
}

/** 웹 주소. 앱과 주소 체계가 달라 shared 는 **가는 곳의 종류**만 돌려준다.
 *  웹에는 `returned`(나갔다 돌아온 셀러용 화면)가 없어 결과 화면으로 간다. 진행 중 · 전량 실패는
 *  작업 첫 주소로 보내고 단계 고르기는 작업 셸(`StageIndex`)에 맡긴다. */
export function notificationHref(line: NotificationLine): string {
  const base = `/jobs/${encodeURIComponent(line.jobId)}`;
  switch (line.target) {
    case "result":
    case "returned":
      return `${base}/result`;
    case "export":
      return `${base}/export`;
    case "history":
      return `${base}/history`;
    case "progress":
      return base;
  }
}

/** 머리 글. 안 읽음이 0건이면 `알림` 하나(PRD `7.6.12` ⓔ · `7.7.7`). */
export function notificationsHeading(unreadCount: number): string {
  return unreadCount > 0 ? `알림 · 안 읽음 ${unreadCount}건` : "알림";
}

/** `W-60` 알림. **전용 화면을 따로 두지 않는다.** 벨을 누르면 화면을 떠나지 않고 그 자리에서
 *  패널이 열린다. 폰은 탭(`A-30`)이고 웹은 드롭다운이다. 기능은 같고 조작 방식만 다르다.
 *
 * ⭐ **줄은 앱 `A-30` 과 같은 함수(`notificationFeed`)로 만든다.** 저장 행(`GET /api/notifications`)과
 * 지금 만드는 중인 작업의 줄을 시각 순으로 섞는다(PRD `7.6.12` ⓓ). 읽음은 계정에 저장되어 앱과 같다.
 *
 * 💰 작업 목록 질문은 **패널이 열려 있을 때만** 나간다(만드는 중 줄용. 예전 판과 같은 비용). 벨 점은
 * `Shell.tsx` 가 알림 질문 하나로 그린다. */
export function NotificationsPanel({ onOpen }: NotificationsPanelProps) {
  const notifications = useNotifications();
  // ⚠️ 앱은 홈과 같은 질문(`HOME_PAGE_SIZE`)을 쓴다. 장수는 shared 한 값에서 읽는다.
  const jobs = useQuery({
    queryKey: ["jobs", "notifications"],
    queryFn: () => listJobs(EMPTY_JOB_SEARCH_FILTER, { limit: NOTIFICATION_JOB_LIMIT }),
  });
  const { readOne, readAll } = useNotificationReads();

  const lines = notificationFeed(notifications.data?.items ?? [], jobs.data?.items ?? [], "web");
  const unreadCount = notifications.data?.unreadCount ?? 0;

  function open(line: NotificationFeedLine) {
    // 읽음 요청이 실패해도 이동을 막지 않는다(ⓔ). 기다리지 않고 바로 간다.
    if (line.notificationId && line.unread) readOne.mutate(line.notificationId);
    onOpen(notificationHref(line));
  }

  return (
    <span className="menupanel notif" role="dialog" aria-label="알림">
      <span className="mhead">
        <span>{notificationsHeading(unreadCount)}</span>
        {/* 안 읽은 행이 없으면 숨긴다(비활성 아님. ⓔ). */}
        {unreadCount > 0 ? (
          <button type="button" className="link" onClick={() => readAll.mutate()}>
            모두 읽음
          </button>
        ) : null}
      </span>

      {notifications.isPending ? (
        /* 불러오는 중을 "알림이 없다"로 말하지 않는다. */
        <div role="status" aria-live="polite" style={{ display: "grid", gap: 9 }}>
          <span className="sr-only">알림을 불러오는 중입니다</span>
          <Skeleton width="88%" />
          <Skeleton width="70%" />
        </div>
      ) : notifications.isError && lines.length === 0 ? (
        /* 조회가 실패한 것과 알림이 없는 것은 다른 사실이다(`W-20` 목록과 같은 갈래). */
        <EmptyState
          art="!"
          tone="bad"
          title="알림을 지금 불러올 수 없어요"
          body="연결이 끊겼거나 잠깐 답이 오지 않았습니다."
          action={<Button label="다시 시도" onClick={() => void notifications.refetch()} />}
        />
      ) : lines.length === 0 ? (
        // 앱 `A-30` 빈 상태와 같은 글이다.
        <EmptyState art="◉" title="아직 알림이 없어요" body="상세페이지가 준비되면 여기에 쌓입니다." />
      ) : (
        <div className="nlist">
          {lines.map((line) => (
            <button
              type="button"
              className={line.unread ? "nitem new" : "nitem"}
              key={line.key}
              aria-label={line.unread ? `안 읽음. ${notificationText(line)}` : notificationText(line)}
              onClick={() => open(line)}
            >
              <span className={`ni ${line.tone}`} aria-hidden="true">
                {line.glyph}
              </span>
              <span>
                <span className="h">{line.title}</span>
                {line.body || line.origin ? (
                  <span className="b">
                    {line.body}
                    {line.origin ? (
                      <>
                        {" · "}
                        <SourceBadge origin={line.origin.word} />
                        {line.origin.suffix}
                      </>
                    ) : null}
                  </span>
                ) : null}
              </span>
              <span className="t mono">{line.at}</span>
            </button>
          ))}
        </div>
      )}

      {/* 이제 참이다. 읽음이 계정에 저장된다(`readAt`). 푸시 경로 `미정` 문장은 2026-10-06(#179)에 걷은
          그대로 싣지 않는다(PRD `7.6.12` ⓖ · `7.7.7`). */}
      <LimitBanner>알림 목록은 앱과 웹이 같습니다. 어느 쪽에서 읽어도 양쪽에서 읽음 처리됩니다.</LimitBanner>
    </span>
  );
}
